import { useState } from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Dropdown, DropdownItem, DropdownSeparator } from '../components/ui';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useT, type MessageKey } from '../i18n';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { useSession } from '../lib/session';

/**
 * Armazón del panel de plataforma.
 *
 * Existe porque el panel se dibujaba **dentro del layout personal**: se entraba
 * a administrar y alrededor seguían «Mis archivos», «Fotos», la papelera, el
 * selector de espacio y el medidor de cuota del propio operador. Administrar la
 * plataforma y usar tu nube son dos trabajos distintos, y mezclarlos obligaba a
 * ir y volver todo el rato para no perderse.
 *
 * Aquí no hay selector de espacio ni buscador de archivos: buscarían en la nube
 * personal de quien administra, que no tiene nada que ver con lo que se está
 * mirando. La única salida al espacio propio es un enlace explícito, y es
 * deliberado que sea sólo uno.
 */

interface Seccion {
  to: string;
  label: MessageKey;
  icon: string;
  end?: boolean;
}

const SECCIONES: Seccion[] = [
  { to: '/admin', label: 'admin.overview', icon: '◇', end: true },
  { to: '/admin/tenants', label: 'tenants.title', icon: '🏢' },
  { to: '/admin/organizations', label: 'admin.organizations', icon: '👥' },
  { to: '/admin/plans', label: 'admin.plans', icon: '🏷' },
  { to: '/admin/storage', label: 'admin.storage', icon: '💾' },
  { to: '/admin/margins', label: 'admin.margins', icon: '📉' },
  { to: '/admin/receipts', label: 'admin.receipts', icon: '🧾' },
  { to: '/admin/audit-logs', label: 'admin.audit', icon: '≡' },
];

export function AdminLayout() {
  const { session, clear } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [menuAbierto, setMenuAbierto] = useState(false);

  if (!session) return null;

  /*
   * La puerta de verdad está en el backend, que comprueba `is_platform_admin`
   * en cada petición. Ésta evita algo distinto: que alguien que no es operador
   * vea el armazón del panel y una pantalla que sólo sabe dar 403. Antes se
   * cargaba entera y fallaba consulta a consulta.
   */
  if (!session.user.isPlatformAdmin) return <Navigate to="/403" replace />;

  async function salir() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    clear();
    navigate('/login');
  }

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      <a href="#main" className="skip-link">
        {t('nav.skipToContent')}
      </a>

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line/50 bg-surface/80 backdrop-blur-xl',
          'transition-transform lg:static lg:translate-x-0',
          menuAbierto ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center gap-2 px-4">
          {/*
            El logo NO vuelve al dashboard personal: dentro del panel, volver
            al principio es volver al resumen del panel. Que el logo te sacara
            del sitio era la mitad del problema.
          */}
          <NavLink to="/admin" className="text-lg font-semibold tracking-tight">
            29100
          </NavLink>
          <Badge tone="warning">{t('admin.badge')}</Badge>
        </div>

        <nav className="mt-4 flex-1 space-y-1 overflow-y-auto px-3" aria-label={t('admin.title')}>
          {SECCIONES.map((seccion) => (
            <NavLink
              key={seccion.to}
              to={seccion.to}
              end={seccion.end}
              onClick={() => setMenuAbierto(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition',
                  isActive
                    ? 'bg-brand/15 text-content shadow-[inset_0_0_0_1px_rgb(var(--c-primary)/25%)]'
                    : 'text-muted hover:bg-surface-2/60 hover:text-content',
                )
              }
            >
              <span aria-hidden="true" className="w-5 text-center">
                {seccion.icon}
              </span>
              {t(seccion.label)}
            </NavLink>
          ))}
        </nav>

        {/*
          La única salida, y explícita. No es un selector de espacios: quien
          administra tiene su propia nube, pero cambiar de una a otra es una
          decisión, no algo que deba pasar por descuido al pulsar el logo.
        */}
        <div className="border-t border-line/50 p-3">
          <Button variant="secondary" size="sm" to="/dashboard" className="w-full">
            {t('admin.exitToMySpace')}
          </Button>
        </div>
      </aside>

      {menuAbierto ? (
        <button
          type="button"
          aria-label={t('nav.closeMenu')}
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setMenuAbierto(false)}
        />
      ) : null}

      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line/40 bg-background/70 px-4 backdrop-blur-xl">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-label={t('nav.openMenu')}
            onClick={() => setMenuAbierto(true)}
          >
            ☰
          </Button>

          <p className="text-sm font-medium">{t('admin.title')}</p>

          <div className="ml-auto flex items-center gap-1">
            <LanguageSwitcher compact />

            <Dropdown
              label={t('nav.account')}
              trigger={() => (
                <Avatar
                  name={`${session.user.firstName ?? ''} ${session.user.lastName ?? ''}`.trim()}
                  email={session.user.email}
                  src={session.user.avatarUrl}
                />
              )}
              header={
                <>
                  <p className="truncate text-sm font-medium">
                    {session.user.firstName ?? session.user.email}
                  </p>
                  <p className="truncate text-xs text-muted">{session.user.email}</p>
                </>
              }
            >
              {({ close }) => (
                <>
                  <DropdownItem
                    onClick={() => {
                      close();
                      navigate('/dashboard');
                    }}
                  >
                    {t('admin.exitToMySpace')}
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      close();
                      navigate('/settings');
                    }}
                  >
                    {t('nav.settings')}
                  </DropdownItem>
                  <DropdownSeparator />
                  <DropdownItem
                    danger
                    onClick={() => {
                      close();
                      void salir();
                    }}
                  >
                    {t('nav.signOut')}
                  </DropdownItem>
                </>
              )}
            </Dropdown>
          </div>
        </header>

        <main id="main" className="flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
